/**
 * GiftCardViewPage.jsx
 *
 * Recipient's secure "floating card" view at /gift-card/view/:viewToken.
 *
 * Security model:
 * - The view token is opaque (random 48 chars) and NOT the redemption code.
 * - Code is always masked on first paint (e.g. "ABCD-XXXX-XXXX-WXYZ").
 * - "Reveal code" button calls a separate rate-limited endpoint (10/min/IP).
 * - Lookup-by-code-and-email is on a different route, rate-limited 5/min.
 *
 * Design: floating gradient card with operator's logo and brand color,
 * balance, transactions, and a Copy button after reveal.
 */
import { useEffect, useState } from "react";
import { apiErrorText } from '../lib/apiError';
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Gift, ChevronLeft, Copy, Check, Eye, EyeOff, Loader2,
  Sparkles, Clock, MinusCircle, PlusCircle, RefreshCw, CalendarClock,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const fmtMoney = (amount, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      currencyDisplay: "narrowSymbol",
    }).format(Number(amount || 0));
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${currency}`;
  }
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const TXN_LABEL = {
  issue: { label: "Issued", color: "text-blue-600", Icon: PlusCircle },
  redeem: { label: "Redeemed", color: "text-red-600", Icon: MinusCircle },
  refund: { label: "Refunded", color: "text-emerald-600", Icon: RefreshCw },
  adjust: { label: "Adjusted", color: "text-amber-600", Icon: RefreshCw },
};

const STATUS_BADGE = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  depleted: "bg-gray-100 text-gray-600 ring-gray-200",
  expired: "bg-amber-50 text-amber-700 ring-amber-200",
  disabled: "bg-red-50 text-red-700 ring-red-200",
};

export default function GiftCardViewPage() {
  const { viewToken } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [revealedCode, setRevealedCode] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/v2/public/gift-card-view/${viewToken}`);
        if (cancelled) return;
        setCard(res.data);
      } catch (e) {
        if (!cancelled) setError(apiErrorText(e, "not_found"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewToken]);

  const handleReveal = async () => {
    if (revealedCode) {
      setRevealedCode(null);
      return;
    }
    setRevealing(true);
    try {
      const res = await axios.post(`${API_URL}/api/v2/public/gift-card-view/reveal-code`, { view_token: viewToken });
      setRevealedCode(res.data.code);
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("Too many attempts. Please wait a moment and try again.");
      } else {
        toast.error("Could not reveal code");
      }
    } finally {
      setRevealing(false);
    }
  };

  const handleCopy = () => {
    if (!revealedCode) return;
    navigator.clipboard.writeText(revealedCode);
    setCopied(true);
    toast.success("Code copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-md p-8 border border-gray-100">
          <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <div className="text-xl font-semibold text-gray-900 mb-2">Gift card not found</div>
          <p className="text-gray-600 mb-6">This link may have expired or never existed.</p>
          <Link to="/gift-card/lookup" className="inline-block px-5 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium">
            Lookup your gift card
          </Link>
        </div>
      </div>
    );
  }

  const primary = card.org_primary_color || "#0f172a";
  const statusClass = STATUS_BADGE[card.status] || STATUS_BADGE.active;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100" data-testid="gift-card-view-page">
      {/* Top bar */}
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 pt-[env(safe-area-inset-top)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <a
            href={`/book.html?org=${card.org_slug}${revealedCode ? `&gift=${encodeURIComponent(revealedCode)}` : ""}`}
            className="text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-full transition-colors"
            data-testid="gift-card-book-now"
          >
            Book now
          </a>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* The floating card */}
        <div className="relative" data-testid="gift-card-floating-card">
          {/* Soft shadow under card */}
          <div className="absolute inset-x-8 -bottom-2 h-8 bg-black/15 blur-2xl rounded-full" />
          <div
            className="relative rounded-3xl p-7 sm:p-9 text-white shadow-2xl overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${primary} 0%, #1e293b 100%)`,
            }}
          >
            {/* Decorative blob */}
            <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full opacity-20"
                 style={{ background: "radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 70%)" }} />

            {/* Header row */}
            <div className="relative flex items-start justify-between mb-6 sm:mb-8">
              <div>
                {card.org_logo_url ? (
                  <img src={card.org_logo_url} alt={card.org_name}
                       className="h-10 w-auto rounded-lg bg-white/10 backdrop-blur-sm p-1.5 mb-2" />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center mb-2">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                )}
                <div className="text-xs uppercase tracking-widest opacity-75">Gift card</div>
                <div className="text-lg font-semibold">{card.org_name}</div>
              </div>
              <span className={`px-2.5 py-1 text-[11px] font-medium rounded-full ring-1 ring-inset bg-white/20 text-white ring-white/30`}>
                {card.status === "active" ? "Active" : card.status.charAt(0).toUpperCase() + card.status.slice(1)}
              </span>
            </div>

            {/* Balance */}
            <div className="relative">
              <div className="text-[11px] uppercase tracking-widest opacity-75 mb-1">Balance</div>
              <div className="text-4xl sm:text-5xl font-bold mb-1" data-testid="gift-card-balance">
                {fmtMoney(card.remaining_balance, card.currency)}
              </div>
              {card.remaining_balance !== card.initial_value && (
                <div className="text-sm opacity-75">of {fmtMoney(card.initial_value, card.currency)} initial value</div>
              )}
            </div>

            {/* Code */}
            <div className="relative mt-7 pt-5 border-t border-white/20">
              <div className="text-[11px] uppercase tracking-widest opacity-75 mb-1.5">Redemption Code</div>
              <div className="flex items-center gap-3">
                <code className="font-mono text-lg sm:text-xl font-semibold tracking-wider flex-1 truncate" data-testid="gift-card-code">
                  {revealedCode || card.masked_code}
                </code>
                <div className="flex gap-1.5">
                  <button
                    onClick={handleReveal}
                    disabled={revealing}
                    className="px-3 py-2 rounded-lg text-xs font-medium bg-white/15 hover:bg-white/25 backdrop-blur-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="gift-card-reveal-btn"
                  >
                    {revealing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (revealedCode ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />)}
                    {revealedCode ? "Hide" : "Reveal"}
                  </button>
                  {revealedCode && (
                    <button
                      onClick={handleCopy}
                      className="px-3 py-2 rounded-lg text-xs font-medium bg-white text-gray-900 hover:bg-gray-100 transition-colors flex items-center gap-1.5"
                      data-testid="gift-card-copy-btn"
                    >
                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Footer details */}
            <div className="relative mt-6 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-widest opacity-75 mb-0.5">From</div>
                <div className="font-medium truncate">{card.purchaser_name || "—"}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest opacity-75 mb-0.5">Expires</div>
                <div className="font-medium">{fmtDate(card.expires_at)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Personal message */}
        {card.personal_message && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <Sparkles className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider text-amber-900/70 font-semibold mb-1.5">Message from {card.purchaser_name || "your friend"}</div>
                <p className="text-amber-900 italic leading-relaxed" data-testid="gift-card-personal-message">"{card.personal_message}"</p>
              </div>
            </div>
          </div>
        )}

        {/* How to use */}
        <div className="mt-6 bg-white border border-gray-100 rounded-2xl shadow-sm p-5 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-3">How to use your gift card</h3>
          <ol className="space-y-2 text-sm text-gray-700">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold flex items-center justify-center">1</span>
              <span>Tap "Book now" above — pick an experience with {card.org_name} and your gift card applies at checkout.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold flex items-center justify-center">2</span>
              <span>Reveal your code on this page and paste it at checkout in the "Gift card" field.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold flex items-center justify-center">3</span>
              <span>Pay any remaining balance with a card — partial use is allowed and your remaining balance stays on this card.</span>
            </li>
          </ol>
        </div>

        {/* Transactions */}
        {card.transactions?.length > 0 && (
          <div className="mt-6 bg-white border border-gray-100 rounded-2xl shadow-sm p-5 sm:p-6" data-testid="gift-card-transactions">
            <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Activity
            </h3>
            <div className="divide-y divide-gray-100">
              {card.transactions.map((t, idx) => {
                const meta = TXN_LABEL[t.txn_type] || TXN_LABEL.adjust;
                const { Icon } = meta;
                return (
                  <div key={idx} className="py-3 flex items-center gap-3 text-sm">
                    <div className={`w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{meta.label}</div>
                      <div className="text-xs text-gray-500">{fmtDate(t.created_at)}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-semibold ${meta.color}`}>
                        {t.txn_type === "redeem" ? "-" : "+"}{fmtMoney(Math.abs(t.amount), t.currency)}
                      </div>
                      <div className="text-xs text-gray-500">balance: {fmtMoney(t.balance_after, t.currency)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-8 flex items-center justify-center gap-1.5">
          <CalendarClock className="w-3 h-3" />
          Powered by BookingFlows • Keep your code safe — anyone with it can redeem.
        </p>
      </main>
    </div>
  );
}
